import { ErrorMessage } from 'formik';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Col, ControlLabel, FormControl, FormGroup, Grid, Modal, Row } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { getPatient, getTopUsedMedicalServicesAPI } from '../../api/agenda';
import { useAgenda, useMedicalServices, useSpecialties, useIsFirstTime, useNearestAvailability } from '../../containers/Agenda/Hooks';
import AppointmentsHistory from '../../containers/Agenda/AppointmentsHistory';
import { CustomFormControl } from '../CustomControls/CustomFormControl';
import { CustomSelect } from "../CustomControls/CustomSelect";
import { PatientPicker } from '../CustomControls/PatientPicker';
import { YesNo } from '../CustomControls/YesNoControls';
import { Save } from "../Icons/Icons";
import { HandleError } from '../Notifications/APIErrorHandler';
import { ExpandableContent } from '../CustomControls/ExpandableContent';
import { FormErrorMessage } from '../Validations/FormValidationErrors';
import { MedicalServiceInfo } from './MedicalServiceInfo';
import { colourStyles, filterServicesBySpecialty, formatSlotLabel, getAvailableTimes, getEarliestAvailableSlot, isValidEventStart, serviceHasSpecialty, validateAvailabilityDate } from './Utils';
import { CustomDate } from '../CustomControls/CustomDateTime';

export const AsistencialAppointmentForm = (props) => {
    const {
        values,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleSubmit,
        resetForm,
        formType,
        masterdata,
    } = props;
    const { t } = useTranslation();

    const calendarView = useRef("days");
    const nearestSlotToken = useRef(0);
    const [medicalInsurances, setMedicalInsurances] = useState([]);
    const [types, setTypes] = useState([]);
    const [filteredMedicalServices, setFilteredMedicalServices] = useState([]);
    const [isLoading, setIsLoading,
        practitioners, getPractitioners, setPractitioners,
        availability, getAvailability, setAvailability] = useAgenda();
    const [isMSLoading, setIsMSLoading, , , ,
        , getMedicalServices, ,
        medicalServicePrice, getMedicalServicePrice, setMedicalServicePrice] = useMedicalServices();
    const [specialties, isSpecialtiesLoading, getMedicalServicesBySpecialties] = useSpecialties();
    const [getIsFirstTime] = useIsFirstTime();
    const [getNearestAvailability, isNearestLoading] = useNearestAvailability();
    const [availableTimes, setAvailableTimes] = useState([]);
    const [selectionMode, setSelectionMode] = useState('auto');
    const [noAvailability, setNoAvailability] = useState(false);
    const [usageMap, setUsageMap] = useState({});
    const [msInputText, setMsInputText] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    // Sobreturno: Agenda solo usa las disponibilidades con sobreturno habilitado y los turnos comunes no ocupan
    // el horario. Antes este formulario siempre pedia la disponibilidad como turno comun.
    const isOverShift = values.visitType?.code?.toUpperCase() === "OVERSHIFT";

    useEffect(() => {
        if (values.patient) {
            _getPatientData("patient", values.patient);
        }
    }, []);

    useEffect(() => {
        getTopUsedMedicalServicesAPI().then(res => {
            const map = {};
            (res.data || []).forEach(item => {
                map[item.medicalServiceId] = item.count;
            });
            setUsageMap(map);
        }).catch(() => {});
    }, []);

    // Auto-recover: if masterdata arrives after patient was already selected, repopulate types
    useEffect(() => {
        if (masterdata && masterdata.length > 0 && values.patient && types.length === 0) {
            _getPatientData(null, values.patient);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [masterdata]);

    useEffect(() => {
        if (selectionMode === 'auto' && values.medicalInsurance && values.medicalService) {
            setFieldValue("practitioner", null);
            setFieldValue("selectedDate", null);
            setFieldValue("selectedTime", null);
            setFieldValue("start", null);
            setFieldValue("end", null);
            setAvailableTimes([]);
            _getNearestSlot(values.medicalService.id, values.medicalInsurance.id, values.medicalInsurance.plan, values.isFirstTime, values.specialty?.id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.medicalInsurance, values.medicalService, selectionMode]);

    //#region Private
    const _getPatientData = async (controlName, selected, medicalRecordNumber) => {
        try {
            const response = await getPatient(medicalRecordNumber ?? selected.medicalRecordNumber);
            const patient = response.data;
            if (controlName)
                setFieldValue(controlName, patient);

            setFieldValue("patientEmail", patient.email ?? '');
            setFieldValue("patientOriginalEmail", patient.email ?? '');

            var options = (masterdata ?? []).filter(x =>
                x.code?.toLowerCase() !== "protocol" &&
                x.code?.toLowerCase() !== "prescreening"
            );
            if (!patient.projects || patient.projects.length === 0)
                options = options.filter(x => x.code?.toLowerCase() !== "project");
            setTypes(options);

            let insurances = [{ id: 0, title: "Particular", plan: 0 }];
            if (patient.medicalInsurance) {
                if (patient.medicalInsurancePlan)
                    insurances.push({ id: patient.medicalInsuranceId, title: `${patient.medicalInsurance} - ${patient.medicalInsurancePlan}`, plan: patient.medicalInsurancePlanId });
                else
                    insurances.push({ id: patient.medicalInsuranceId, title: `${patient.medicalInsurance}`, plan: patient.medicalInsurancePlanId });
            }
            setMedicalInsurances(insurances);
        } catch (error) {
            console.log(error);
        }
    }

    const _getMedicalServicesBySpecialty = async (specialtyId) => {
        try {
            setIsMSLoading(true);
            let res;
            if (!specialtyId) {
                res = await getMedicalServices();
            } else {
                res = await getMedicalServicesBySpecialties([specialtyId]);
            }
            setFilteredMedicalServices(filterServicesBySpecialty(res.data?.data, specialtyId));
            setIsMSLoading(false);
        } catch (ex) {
            console.log(ex);
            setFilteredMedicalServices([]);
            setIsMSLoading(false);
        }
    }

    const _getMedicalServicesData = async (medicalServiceId, medicalInsuranceId, medicalInsurancePlanId) => {
        if (!medicalServiceId)
            return;
        try {
            setIsMSLoading(true);
            var res = await getMedicalServicePrice(medicalServiceId, medicalInsuranceId, medicalInsurancePlanId ?? 0);
            setMedicalServicePrice(res.data.data);
            setIsMSLoading(false);
        } catch (ex) {
            console.log(ex);
            setIsMSLoading(false);
        }
    }

    const _getPractitionersForMedicalService = async (medicalServiceId, medicalInsuranceId, specialtyId) => {
        if (!medicalServiceId) {
            setPractitioners([]);
            return;
        }
        try {
            setIsLoading(true);
            var res = await getPractitioners(medicalServiceId, medicalInsuranceId, specialtyId);
            var users = res.data?.data.map(x => x.practitioner).filter(x => !x.practitioner);
            setPractitioners(users ?? []);
            setIsLoading(false);
        } catch (ex) {
            console.log(ex);
            setPractitioners([]);
            setIsLoading(false);
        }
    }

    // token: si mientras se buscaba hubo otro cambio (otro profesional, otra busqueda automatica), la respuesta
    // se descarta para no pisar la disponibilidad vigente. Devuelve la disponibilidad, o null si se descarto o fallo.
    const _getAvailability = async (userId, medicalServiceId, medicalInsuranceId, medicalInsurancePlanId, isFirstTime, isOverShift, specialtyId, token = nearestSlotToken.current) => {
        try {
            setIsLoading(true);
            var res = await getAvailability(userId, medicalServiceId, medicalInsuranceId, medicalInsurancePlanId, isFirstTime, isOverShift ?? false, specialtyId);
            if (token !== nearestSlotToken.current) return null;
            setAvailability(res.data);
            return res.data;
        } catch (ex) {
            if (token !== nearestSlotToken.current) return null;
            setAvailability([]);
            console.log(ex);
            return null;
        } finally {
            if (token === nearestSlotToken.current) setIsLoading(false);
        }
    }

    const _checkIsFirstTime = async (medicalRecordNumber, medicalServiceId) => {
        const result = await getIsFirstTime(medicalRecordNumber, medicalServiceId);
        setFieldValue("isFirstTime", result.isFirstTime);
        return result.isFirstTime;
    }

    const _getNearestSlot = async (medicalServiceId, medicalInsuranceId, medicalInsurancePlanId, isFirstTime, specialtyId) => {
        if (!medicalServiceId || medicalInsuranceId == null) return;
        const token = ++nearestSlotToken.current;
        setNoAvailability(false);
        const result = await getNearestAvailability(medicalServiceId, medicalInsuranceId, medicalInsurancePlanId ?? 0, isFirstTime ?? false, specialtyId > 0 ? specialtyId : null, isOverShift);
        if (token !== nearestSlotToken.current) return; // user interacted manually — discard stale result
        if (!result) {
            setNoAvailability(true);
            setSelectionMode('manual');
            return;
        }
        // Find the matching practitioner object already loaded in `practitioners`
        // We need availability data for that practitioner too
        const matchedPractitioner = practitioners.find(p => p.userId === result.practitionerUserId?.toString()?.toLowerCase() ||
            p.userId?.toString()?.toLowerCase() === result.practitionerUserId?.toString()?.toLowerCase());
        if (matchedPractitioner) {
            setFieldValue("practitioner", matchedPractitioner);
        } else {
            // Build a minimal practitioner shape from the result so the form can show it
            setFieldValue("practitioner", { userId: result.practitionerUserId, fullName: result.practitionerFullName });
        }
        // Set date & time from nearest slot result
        const nearestDate = moment(result.date);
        setFieldValue("selectedDate", nearestDate);
        // Fetch availability so the calendar/time picker is populated
        try {
            const avRes = await getAvailability(result.practitionerUserId, medicalServiceId, medicalInsuranceId, medicalInsurancePlanId ?? 0, isFirstTime ?? false, isOverShift, specialtyId);
            if (token !== nearestSlotToken.current) return; // cancelled while fetching availability
            setAvailability(avRes.data);
            // Pre-select the time slot
            const dayAvail = avRes.data?.availableDates?.find(d => moment(d.date).format('DD/MM/YYYY') === nearestDate.format('DD/MM/YYYY') && d.available);
            if (dayAvail) {
                const times = dayAvail.availableAppointments.filter(a => a.available);
                setAvailableTimes(times);
                // Por inicio y fin: con disponibilidades superpuestas puede haber dos horarios con el mismo inicio
                // y distinta duracion ("HH:mm:ss" o TimeSpan, se compara HH:mm).
                const hhmm = time => time?.substring(0, 5);
                const matchedSlot = times.find(a => hhmm(a.appointmentInit) === hhmm(result.slotInit) && hhmm(a.appointmentEnd) === hhmm(result.slotEnd))
                    ?? times.find(a => hhmm(a.appointmentInit) === hhmm(result.slotInit));
                if (matchedSlot)
                    _setSlot(nearestDate, matchedSlot);
            }
        } catch (ex) {
            console.log(ex);
        }
    }

    const _setSlot = (date, slot) => {
        let datastart = date.toDate();
        let dataend = date.toDate();
        datastart.setHours(slot.appointmentInit.split(":")[0]);
        datastart.setMinutes(slot.appointmentInit.split(":")[1]);
        dataend.setHours(slot.appointmentEnd.split(":")[0]);
        dataend.setMinutes(slot.appointmentEnd.split(":")[1]);
        setFieldValue("start", datastart);
        setFieldValue("end", dataend);
        setFieldValue("selectedTime", slot);
    }
    //#endregion

    //#region Events
    const handlePatientChange = (controlName, selected) => {
        if (selected !== null) {
            _getPatientData(controlName, selected);
        } else {
            resetForm();
        }
    }

    const handleTypeChange = (_controlName, selectedValue) => {
        if (selectedValue === null)
            return;

        setFieldValue("visitType", selectedValue);
        setFieldValue("isFirstTime", null);
        setFieldValue("specialty", null);
        setFieldValue("medicalInsurance", null);
        setFieldValue("medicalService", null);
        setFieldValue("practitioner", null);
        setFieldValue("selectedDate", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        setMedicalServicePrice(null);
        _getMedicalServicesBySpecialty(null);
    }

    const handleSpecialtyChange = (controlName, selectedValue) => {
        setFieldValue(controlName, selectedValue);
        const specialtyId = selectedValue?.id || null;
        const currentService = values.medicalService;
        // Se conserva el servicio elegido si sigue siendo válido para la nueva especialidad
        const keepService = !!currentService && (!specialtyId || serviceHasSpecialty(currentService, specialtyId));
        if (!keepService) {
            setFieldValue("medicalService", null);
            setMedicalServicePrice(null);
            setMsInputText('');
        }
        setFieldValue("practitioner", null);
        setFieldValue("selectedDate", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        nearestSlotToken.current++;
        setAvailability([]);
        setAvailableTimes([]);
        setNoAvailability(false);
        setPractitioners([]);
        _getMedicalServicesBySpecialty(specialtyId);
        if (keepService && values.medicalInsurance) {
            _getPractitionersForMedicalService(currentService.id, values.medicalInsurance.id, specialtyId);
        } else if (!keepService) {
            setFieldValue("medicalInsurance", null);
        }
    }

    const handleMedicalInsurance = (controlName, selectedValue) => {
        setFieldValue(controlName, selectedValue);
        setFieldValue("practitioner", null);
        setFieldValue("selectedDate", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        setAvailability([]);
        setAvailableTimes([]);
        setNoAvailability(false);
        setSelectionMode('auto');
        if (selectedValue && values.medicalService) {
            _getMedicalServicesData(values.medicalService.id, selectedValue.id, selectedValue.plan);
            _getPractitionersForMedicalService(values.medicalService.id, selectedValue.id, values.specialty?.id);
        } else {
            setMedicalServicePrice(null);
            setPractitioners([]);
        }
    }

    const handleMedicalService = async (controlName, selectedValue) => {
        setFieldValue(controlName, selectedValue);
        setFieldValue("medicalInsurance", null);
        setFieldValue("practitioner", null);
        setFieldValue("selectedDate", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        setMedicalServicePrice(null);
        setPractitioners([]);
        setMsInputText('');

        if (selectedValue) {
            // Auto-fill specialty if not yet selected, or if "Sin Especialidad" was selected manually
            if (!values.specialty || values.specialty.id === 0) {
                const svcSpecialty = selectedValue.specialties?.[0];
                if (svcSpecialty) {
                    setFieldValue("specialty", { id: svcSpecialty.specialtyId, name: svcSpecialty.specialtyName });
                } else {
                    setFieldValue("specialty", { id: 0, name: "Sin Especialidad" });
                }
            }
            // Auto-detect isFirstTime
            if (values.patient) {
                _checkIsFirstTime(values.patient.medicalRecordNumber, selectedValue.id);
            }
        }
    }

    const handlePractitionerChange = async (controlName, selectedValue) => {
        const token = ++nearestSlotToken.current; // cancel any in-flight _getNearestSlot
        setFieldValue(controlName, selectedValue);
        setFieldValue("selectedDate", null);
        setFieldValue("selectedTime", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        setAvailability([]);
        setAvailableTimes([]);

        const data = await _getAvailability(
            selectedValue.userId,
            values.medicalService?.id,
            values.medicalInsurance?.id,
            values.medicalInsurance?.plan,
            values.isFirstTime ?? false,
            isOverShift,
            values.specialty?.id,
            token
        );

        // Automatico (spec agenda-nuevo-turno.md, Paso 9, Opcion A): se sigue en automatico y la fecha/hora se
        // ajustan al turno mas proximo del profesional elegido. Sin turnos libres queda "No hay horario disponible".
        if (selectionMode !== 'auto' || !data)
            return;

        const earliest = getEarliestAvailableSlot(data);
        if (earliest) {
            setFieldValue("selectedDate", earliest.date);
            setAvailableTimes(earliest.times);
            _setSlot(earliest.date, earliest.slot);
        }
    }

    const handleDate = (name, date) => {
        if (!moment.isMoment(date))
            return;

        setFieldValue(name, date);
        setFieldValue("selectedTime", null);
        setFieldValue("start", null);
        setFieldValue("end", null);

        var availableDate = availability.availableDates?.filter(x => moment(x.date).format('DD/MM/YYYY') === date.format('DD/MM/YYYY') && x.available);
        if (availableDate && availableDate.length > 0) {
            var availableTimesAux = availableDate[0].availableAppointments.filter(x => x.available);
            setAvailableTimes(availableTimesAux);
            return;
        }
        setAvailableTimes([]);
    }

    // _setSlot tambien guarda selectedTime, que es lo que muestra el select: sin eso el select quedaba vacio,
    // o con el horario que habia propuesto Automatico, aunque start/end ya fueran los elegidos.
    const handleTime = (_controlName, selectedValue) => {
        if (values.selectedDate && selectedValue)
            _setSlot(values.selectedDate, selectedValue);
    }

    const handleAvailabilityDate = (current, selected) => {
        if (calendarView.current !== "days" || values.visitType?.code?.toLowerCase() === "order")
            return true;
        return validateAvailabilityDate(current, selected, availability);
    }

    const onNavigate = (selectedView) => {
        calendarView.current = selectedView;
    }

    const handleIsFirstTimeChange = (controlName, value) => {
        setFieldValue(controlName, value);
        // Reset date/time slots — they depend on appointment duration which changes with isFirstTime
        setFieldValue("selectedDate", null);
        setFieldValue("selectedTime", null);
        setFieldValue("start", null);
        setFieldValue("end", null);
        setAvailableTimes([]);

        if (!values.medicalService || values.medicalInsurance == null) return;

        if (selectionMode === 'auto') {
            setFieldValue("practitioner", null);
            _getNearestSlot(
                values.medicalService.id,
                values.medicalInsurance.id,
                values.medicalInsurance.plan,
                value,
                values.specialty?.id
            );
        } else if (values.practitioner) {
            _getAvailability(
                values.practitioner.userId,
                values.medicalService.id,
                values.medicalInsurance.id,
                values.medicalInsurance.plan,
                value,
                isOverShift,
                values.specialty?.id
            );
        }
    }

    // Solo el clic en el radio: al pasar a Manual se descarta lo que propuso Automatico y se elige de cero.
    // Los pasos a manual por cambiar la fecha propuesta o por falta de disponibilidad no pasan por aca y
    // conservan lo cargado.
    const handleSelectionModeChange = (mode) => {
        if (mode === 'manual') {
            nearestSlotToken.current++; // cancel any in-flight _getNearestSlot
            setFieldValue("practitioner", null);
            setFieldValue("selectedDate", null);
            setFieldValue("selectedTime", null);
            setFieldValue("start", null);
            setFieldValue("end", null);
            setAvailability([]);
            setAvailableTimes([]);
            setNoAvailability(false);
        }
        setSelectionMode(mode);
    }

    const onSubmit = (e) => {
        e.preventDefault();
        if (!isValidEventStart(values.start)) {
            HandleError("calendarErrors.Event_Create_StartLowerThanNow");
            return;
        }
        handleSubmit();
    }
    //#endregion

    return (
        <Modal show={true} onHide={props.onClose} backdrop='static' bsSize="large">
            <Modal.Header closeButton>
                <Modal.Title>
                    {props.onBack && (
                        <button
                            type="button"
                            onClick={props.onBack}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 10px 0 0', color: 'inherit', fontSize: '18px', verticalAlign: 'middle' }}
                            title="Volver"
                        >
                            &#8592;
                        </button>
                    )}
                    {t('calendar.newAsistencialAppointmentTitle')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={onSubmit} autoComplete="off">
                    <Grid fluid>
                        {/* Step 1: Patient + New Patient + History in same row */}
                        <Row style={{ alignItems: 'flex-end' }}>
                            <Col sm={6}>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <ControlLabel>{t("calendar.patient")}</ControlLabel>
                                    <PatientPicker
                                        autoFocus={!values.patient}
                                        id="asistencial-patient-picker"
                                        name="patient"
                                        multiple={false}
                                        placeholder={t("filters.patient")}
                                        onChange={handlePatientChange}
                                        selected={values.patient ? [values.patient] : []}
                                    />
                                    <ErrorMessage name="patient" component={FormErrorMessage} />
                                </FormGroup>
                            </Col>
                            <Col sm={3} style={{ paddingTop: '24px' }}>
                                <NavLink
                                    to="/admin/database/create?redirectTo=agenda"
                                    className="btn btn-block"
                                    activeClassName=""
                                    style={{ color: '#337ab7', backgroundColor: '#fff', border: '1px solid #337ab7', borderRadius: '4px' }}
                                >
                                    + {t("calendar.newPatient")}
                                </NavLink>
                            </Col>
                            <Col sm={3} style={{ paddingTop: '24px' }}>
                                <Button
                                    bsStyle="primary"
                                    block
                                    disabled={!values.patient}
                                    onClick={() => setShowHistory(prev => !prev)}
                                    type="button"
                                >
                                    {showHistory && <span style={{ marginRight: '6px' }}>&times;</span>}
                                    {t("calendar.patientAppointments")}
                                </Button>
                            </Col>
                        </Row>

                        {/* Patient history — toggled by the button above, rendered directly (no internal toggle) */}
                        {values.patient && showHistory && (
                            <Row>
                                <Col sm={12} style={{ marginTop: "10px" }}>
                                    <AppointmentsHistory medicalRecordNumber={values.patient.medicalRecordNumber} card={false} />
                                </Col>
                            </Row>
                        )}

                        {/* Step 2: Visit Type (Paso 4) */}
                        {values.patient && (
                            <Row>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t("calendar.visitType")}</ControlLabel>
                                        <CustomSelect
                                            value={values.visitType}
                                            name="visitType"
                                            placeholder={t('commons.select')}
                                            isDisabled={!values.patient || !types}
                                            options={types}
                                            onChange={handleTypeChange}
                                            isMulti={false}
                                            styles={colourStyles}
                                        />
                                        <ErrorMessage name="visitType" component={FormErrorMessage} />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}

                        {/* Pasos 5+6: Especialidad + Servicio Médico (simultáneos, bidireccionales) */}
                        {values.visitType && (
                            <Row>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t("calendar.specialty")}</ControlLabel>
                                        <CustomSelect
                                            value={values.specialty}
                                            name="specialty"
                                            placeholder={t('commons.select')}
                                            isClearable
                                            isLoading={isSpecialtiesLoading}
                                            options={[{ id: 0, name: "Sin Especialidad" }, ...(specialties || [])]}
                                            getOptionLabel={(option) => option.name ? option.name.charAt(0).toUpperCase() + option.name.slice(1) : option.name}
                                            getOptionValue={(option) => option.id}
                                            onChange={handleSpecialtyChange}
                                            isMulti={false}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t("calendar.medicalServiceInfo.medicalService")}</ControlLabel>
                                        <CustomSelect
                                            value={values.medicalService}
                                            name="medicalService"
                                            placeholder={t('commons.select')}
                                            options={[...filteredMedicalServices]
                                                .filter(s => !msInputText || s.serviceNameReference?.toLowerCase().includes(msInputText.toLowerCase()))
                                                .sort((a, b) => (usageMap[b.id] || 0) - (usageMap[a.id] || 0))
                                                .slice(0, 10)}
                                            isLoading={isMSLoading}
                                            getOptionLabel={(option) => option.serviceNameReference ? option.serviceNameReference.charAt(0).toUpperCase() + option.serviceNameReference.slice(1) : option.serviceNameReference}
                                            getOptionValue={(option) => option.id}
                                            onChange={handleMedicalService}
                                            isMulti={false}
                                            inputValue={msInputText}
                                            onInputChange={(val) => setMsInputText(val)}
                                            filterOption={() => true}
                                            noOptionsMessage={() => t('commons.noResults')}
                                        />
                                        <ErrorMessage name="medicalService" component={FormErrorMessage} />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}

                        {/* Paso 7+8: ¿Es primera vez? + Particular / Obra Social en la misma fila */}
                        {values.visitType && (
                            <Row>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t("calendar.appointmentType")}</ControlLabel>
                                        <CustomSelect
                                            value={values.medicalInsurance}
                                            name="medicalInsurance"
                                            placeholder={t('commons.select')}
                                            options={medicalInsurances}
                                            onChange={handleMedicalInsurance}
                                            isMulti={false}
                                        />
                                        <ErrorMessage name="medicalInsurance" component={FormErrorMessage} />
                                    </FormGroup>
                                </Col>
                                {values.visitType.code?.toLowerCase() !== "studies" &&
                                    values.visitType.code?.toLowerCase() !== "order" && (
                                    <Col sm={6}>
                                        <FormGroup>
                                            <ControlLabel style={{ marginLeft: '10px' }}>{t("calendar.isFirstTime")}</ControlLabel>
                                            <br />
                                            <YesNo
                                                value={values.isFirstTime}
                                                onChange={handleIsFirstTimeChange}
                                                name="isFirstTime" />
                                            <ErrorMessage name="isFirstTime" component={FormErrorMessage} />
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>
                        )}

                        {/* Medical Service Price Info */}
                        {values.medicalService && values.medicalInsurance && medicalServicePrice && (
                            <MedicalServiceInfo medicalService={medicalServicePrice} isLoading={isMSLoading} inline={true} />
                        )}

                        {/* Paso 9: Profesional + Fecha/Hora */}
                        {values.medicalInsurance && (
                            <div style={{ backgroundColor: '#EBF3FF', border: '1px solid #C5D8F0', borderRadius: '8px', padding: '12px 16px 4px 16px', marginTop: '12px', marginBottom: '12px' }}>
                                {/* Opción A / B radio */}
                                <Row>
                                    <Col sm={12}>
                                        <FormGroup>
                                            <ControlLabel>{t("calendar.selectionMode")}</ControlLabel>
                                            <div>
                                                <label style={{ marginRight: '20px', fontWeight: 'normal' }}>
                                                    <input
                                                        type="radio"
                                                        name="selectionMode"
                                                        value="auto"
                                                        checked={selectionMode === 'auto'}
                                                        onChange={() => handleSelectionModeChange('auto')}
                                                        style={{ marginRight: '6px' }}
                                                    />
                                                    {t("calendar.selectionAuto")}
                                                </label>
                                                <label style={{ fontWeight: 'normal' }}>
                                                    <input
                                                        type="radio"
                                                        name="selectionMode"
                                                        value="manual"
                                                        checked={selectionMode === 'manual'}
                                                        onChange={() => handleSelectionModeChange('manual')}
                                                        style={{ marginRight: '6px' }}
                                                    />
                                                    {t("calendar.selectionManual")}
                                                </label>
                                            </div>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {isNearestLoading && selectionMode === 'auto' && (
                                    <Row>
                                        <Col sm={12}>
                                            <p className="text-muted">
                                                <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />
                                                {t("calendarNotifications.searchingAvailability")}
                                            </p>
                                        </Col>
                                    </Row>
                                )}
                                {noAvailability && (
                                    <Row>
                                        <Col sm={12}>
                                            <p className="text-warning">{t("calendarNotifications.noAvailabilityFound")}</p>
                                        </Col>
                                    </Row>
                                )}

                                <Row>
                                    <Col sm={6}>
                                        <FormGroup>
                                            <ControlLabel>{t("calendar.practitioner")}</ControlLabel>
                                            <CustomSelect
                                                name="practitioner"
                                                value={values.practitioner}
                                                isLoading={isLoading || isNearestLoading}
                                                isDisabled={!values.medicalService}
                                                placeholder={t('filters.professional')}
                                                options={practitioners}
                                                getOptionLabel={(option) => option.fullName}
                                                getOptionValue={(option) => option.userId}
                                                onChange={handlePractitionerChange}
                                                isMulti={false}
                                            />
                                            <ErrorMessage name="practitioner" component={FormErrorMessage} />
                                        </FormGroup>
                                    </Col>

                                    {/* Date & Time — shown once practitioner is set */}
                                    {values.practitioner && (
                                        <Col sm={6}>
                                            <FormGroup>
                                                <ControlLabel>{t("calendar.dayAndTime")}</ControlLabel>
                                                <div className="row">
                                                    <div className="col-xs-5">
                                                        <CustomDate
                                                            name="selectedDate"
                                                            inputProps={{ placeholder: "Fecha" }}
                                                            closeOnSelect={true}
                                                            value={values.selectedDate}
                                                            onChange={(name, date) => {
                                                                if (selectionMode === 'auto') {
                                                                    nearestSlotToken.current++;
                                                                    setSelectionMode('manual');
                                                                }
                                                                handleDate(name, date);
                                                            }}
                                                            isValidDate={handleAvailabilityDate}
                                                            onNavigate={onNavigate}
                                                            onBlur={() => setFieldTouched("selectedDate", true)}
                                                        />
                                                        <ErrorMessage name="selectedDate" component={FormErrorMessage} />
                                                    </div>
                                                    <div className="col-xs-7">
                                                        {!availableTimes || availableTimes.length === 0 ?
                                                            <ControlLabel>{t("calendarNotifications.noAppointmentAvailable")}</ControlLabel> :
                                                            <CustomSelect
                                                                value={values.selectedTime}
                                                                name="selectedTime"
                                                                placeholder={t('commons.select')}
                                                                getOptionLabel={formatSlotLabel}
                                                                getOptionValue={(option) => `${option.appointmentInit}-${option.appointmentEnd}`}
                                                                options={availableTimes}
                                                                onChange={handleTime}
                                                                isMulti={false}
                                                            />
                                                        }
                                                        <ErrorMessage name="start" component={FormErrorMessage} />
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        )}

                        {/* Paso 10: Notificaciones */}
                        {values.medicalInsurance && (
                            <Row>
                                <Col sm={12}>
                                    <FormGroup>
                                        <label style={{ fontWeight: 'normal' }}>
                                            <input
                                                type="checkbox"
                                                checked={values.notifyPatient || false}
                                                onChange={(e) => setFieldValue("notifyPatient", e.target.checked)}
                                                style={{ marginRight: 6 }}
                                            />
                                            {t('calendar.notifyPatient')}
                                        </label>
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}
                        {values.medicalInsurance && values.notifyPatient && (
                            <Row>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t('calendar.patientEmail')}</ControlLabel>
                                        <CustomFormControl
                                            value={values.patientEmail}
                                            type="email"
                                            name="patientEmail"
                                            placeholder={t('calendar.patientEmail')}
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t('calendar.aclaracionesPaciente')}</ControlLabel>
                                        <CustomFormControl
                                            value={values.patientNotes}
                                            type="textarea"
                                            name="patientNotes"
                                            placeholder={t('calendar.aclaracionesPaciente')}
                                            rows={2}
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}

                        {/* Paso 11: Notas y Ubicación */}
                        {values.medicalInsurance && (
                            <Row>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t('calendar.internalNotes')}</ControlLabel>
                                        <CustomFormControl
                                            value={values.desc}
                                            type="textarea"
                                            name="desc"
                                            placeholder={t('calendar.internalNotes')}
                                            rows={3}
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={6}>
                                    <FormGroup>
                                        <ControlLabel>{t('calendar.location')}</ControlLabel>
                                        <CustomFormControl
                                            value={values.location}
                                            type="text"
                                            name="location"
                                            placeholder={t('calendar.location')}
                                            onChange={setFieldValue}
                                            onBlur={setFieldTouched}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}
                    </Grid>
                    <div className="modal-footer">
                        <Button
                            bsStyle="success"
                            type="submit"
                            disabled={!values.practitioner || !values.start || !values.end || isSubmitting}
                        >
                            <Save /> {t("buttons.save")}
                        </Button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
}

AsistencialAppointmentForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    currentPatient: PropTypes.object,
    formType: PropTypes.string,
};
